import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'

import type { DeviceWithStatus } from '../../types/DeviceMain'
import { colors } from '../../constants'
import { FileUtils } from '../../utils'

export const Icon = styled(FontAwesomeIcon)`
    ${({ color }) => (color ? `color: ${color}` : `color: ${colors.blue}`)}
`

function DeviceWarning({ device }: { device: DeviceWithStatus }) {
    const { t } = useTranslation()

    const {
        device_type,
        hdmi_connected,
        available_storage = 0,
        total_storage = 0,
        cdd_last_online = 0,
        cdd_last_failed_to_connect = 0,
        out_of_storage_at,
    } = device;

    // If the player reported that it does not have enough storage to update content
    if (out_of_storage_at) {
        return (
            <Icon
                color={colors.red}
                icon="exclamation-triangle"
                title={t('device-status-out-storage-content')}
            />
        )
    }

    // Server detected the device to be online in the last hour (65 mins actually,
    // as the reports are sent every hour. Let's give it 5 more minutes to process reports)
    const onlineInTheLastWeek = Date.now() - cdd_last_online < 1000 * 60 * 60 * 24 * 7

    // Server failed to connect to the device in the 5 seconds after it detected
    // the device was online
    const failedToConnect = Math.abs(cdd_last_online - cdd_last_failed_to_connect) <= 5000

    if (onlineInTheLastWeek && failedToConnect) {
        return (
            <Icon
                color={colors.orange}
                icon="exclamation-triangle"
                title={t('device-status-server-fails-to-connect')}
            />
        )
    }

    const freeStorage = available_storage / total_storage;

    if (!isNaN(freeStorage) && freeStorage <= 0.1) {
        const displayFreeStorage = FileUtils.humanFileSize(available_storage);

        return (
            <Icon
                color={colors.orange}
                icon="exclamation-triangle"
                title={t('device-status-out-storage', { size: displayFreeStorage })}
            />
        )
    }

    const isValidType = typeof hdmi_connected === 'boolean' || typeof hdmi_connected === 'number'
    const isTizen = device_type === 'TIZEN'

    if (isValidType && !hdmi_connected && !isTizen) {
        return (
            <Icon
                color={colors.greydark}
                icon="exclamation-triangle"
                title={t('device-status-hdmi-disconnected')}
            />
        )
    }

    return null
}

export default DeviceWarning
